"use client";

import { useQuery } from "@apollo/client";
import { acceptedTermsOfServiceQuery } from "./queries";
import { CURRENT_TERMS_OF_SERVICE } from "./UserTermsOfService";

interface AcceptedTermsOfServiceData {
  acceptedCurrentTermsOfService: { id: string }[];
}

interface AcceptedTermsOfServiceVariables {
  input: {
    userId: string;
    typeOfTos?: string;
  };
}

/**
 * Hook to check if a user has accepted the current terms of service.
 * This makes a fresh API call to verify acceptance status, bypassing any cached session data.
 * Use this when you need to verify terms acceptance after the user returns from accepting terms
 * in another app.
 *
 * Callers must branch on `checkFailed` before acting on `hasAcceptedTerms`.
 * The two are not interchangeable: `hasAcceptedTerms` is false both for a user
 * who genuinely has not accepted *and* for a check that never completed, so
 * treating it alone as authoritative re-prompts a user who already accepted —
 * and if the failure is an auth one, the acceptance they submit fails the same
 * way, which is the accept/reload loop.
 *
 * @param userId - The user ID to check terms acceptance for
 * @param skip - If true, skips the query (useful when userId is not yet available)
 * @returns hasAcceptedTerms, checkFailed, loading state, error, and refetch
 */
export function useTermsAcceptanceCheck(
  userId: string | undefined,
  skip = false,
) {
  const { data, loading, error, refetch } = useQuery<
    AcceptedTermsOfServiceData,
    AcceptedTermsOfServiceVariables
  >(acceptedTermsOfServiceQuery, {
    variables: {
      input: {
        userId: userId ?? "",
        typeOfTos: CURRENT_TERMS_OF_SERVICE,
      },
    },
    // Always fetch from network to bypass Apollo cache and session cache issues
    fetchPolicy: "network-only",
    // Skip if no userId or if explicitly skipped
    skip: skip || !userId,
  });

  // `data` is undefined both before the first response and after a failed one,
  // so this alone cannot tell "has not accepted" from "could not check".
  const hasAcceptedTerms =
    (data?.acceptedCurrentTermsOfService?.length ?? 0) > 0;

  // The check did not complete, so acceptance is unknown rather than false.
  // In prod this is the API rejecting the query as Unauthorized because the
  // request carried no auth token at all — NextAuth reports `authenticated`
  // while the token the API needs is not yet available.
  const checkFailed = Boolean(error) && data === undefined;

  return {
    hasAcceptedTerms,
    checkFailed,
    loading,
    error,
    refetch,
  };
}
